import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO, BRAND } from "../data/content";
import { scrollToHash } from "./Navbar";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const RotatingBadge = () => (
  <div className="absolute -bottom-10 -left-10 hidden h-32 w-32 items-center justify-center rounded-full bg-white shadow-xl shadow-cafe-dark/10 md:flex">
    <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full animate-spin-slow">
      <defs>
        <path id="oesca-circle" d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0" />
      </defs>
      <text className="fill-cafe-dark font-mono text-[9.5px] uppercase tracking-[0.24em]">
        <textPath href="#oesca-circle">
          Oesca Construcciones · Ingeniería · Obra Pesada ·
        </textPath>
      </text>
    </svg>
    <svg viewBox="0 0 44 44" className="h-10 w-10" aria-hidden="true">
      <rect x="16" y="2" width="24" height="14" fill="#55453E" />
      <rect x="2" y="15" width="24" height="14" fill="#A77C5A" transform="rotate(-4 14 22)" />
      <rect x="14" y="28" width="24" height="14" fill="#55453E" />
    </svg>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-cream pt-24"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-0 h-full w-px bg-cafe-line/50" />
        <div className="absolute left-2/4 top-0 hidden h-full w-px bg-cafe-line/50 lg:block" />
        <div className="absolute left-3/4 top-0 hidden h-full w-px bg-cafe-line/50 lg:block" />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-12 items-center gap-10 px-4 pb-24 pt-10 sm:px-6 lg:px-8">
        <motion.div style={{ opacity: fade }} className="col-span-12 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 flex items-center gap-4"
          >
            <span className="h-px w-14 bg-cafe" />
            <span
              data-testid="hero-eyebrow"
              className="font-mono text-[11px] font-semibold uppercase tracking-[0.32em] text-cafe"
            >
              {HERO.eyebrow}
            </span>
          </motion.div>

          <h1 data-testid="hero-title" className="text-cafe-dark">
            <MaskedLine
              delay={0.25}
              className="font-display text-[clamp(4.2rem,13vw,11rem)] font-semibold leading-[0.92] tracking-tight"
            >
              OESCA
            </MaskedLine>
            <MaskedLine
              delay={0.4}
              className="mt-2 font-sans text-[clamp(1rem,3vw,1.9rem)] font-semibold uppercase tracking-[0.52em] text-cafe"
            >
              Construcciones
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-intro"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="font-bodoni mt-8 max-w-xl text-lg leading-relaxed text-cafe-muted sm:text-xl"
          >
            {HERO.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-cotizacion"
              onClick={(e) => { e.preventDefault(); scrollToHash("#contacto"); }}
              className="group flex items-center gap-2 rounded-full bg-cafe-dark px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-cafe-deep"
            >
              Solicitar Cotización
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-conocenos"
              onClick={(e) => { e.preventDefault(); scrollToHash("#quienes-somos"); }}
              className="group flex items-center gap-2 rounded-full border border-cafe-dark/25 px-7 py-3.5 text-sm font-semibold text-cafe-dark transition-colors duration-300 hover:border-cafe hover:text-cafe"
            >
              Conócenos
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative col-span-12 mt-6 lg:col-span-5 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div aria-hidden="true" className="absolute -right-4 -top-4 h-full w-full border border-cafe" />
            <div className="relative overflow-hidden">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src="/img/hero-obra.webp"
                alt="Trabajador de OESCA nivelando terreno en obra al atardecer"
                data-testid="hero-image"
                className="h-[46vh] w-full object-cover sm:h-[58vh] lg:h-[64vh]"
              />
              <div className="absolute inset-0 bg-cafe-dark/10" />
            </div>
            <RotatingBadge />
            <div className="mt-6 flex items-baseline justify-between border-t border-cafe-line pt-4 md:pl-36">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cafe-muted">
                Obra en proceso
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cafe">
                {BRAND.location}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToHash("#quienes-somos")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-cafe-muted transition-colors duration-300 hover:text-cafe lg:flex"
        aria-label="Desplazarse hacia abajo"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Desliza</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};
